/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable camelcase */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { FormControlLabel } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, change as changeFieldValue, formValueSelector } from "redux-form";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Settings page components
import FormField from "components/FormField";
import ColorPickerField from "components/ColorPickerField/ColorPickerField";
import { newAlertMessage } from "store/actions/globalAlertActions";

const FORM_NAME = "CustomAppMobile";

const DEFAULT_WELCOME_BG = "#28363f";
const DEFAULT_ICON_COLOR = "#02A0FF";
const DEFAULT_APP_BACKGROUND_COLOR = "#F0EEEA";
const DEFAULT_CARD_COLOR = "#F0EEEA";
const DEFAULT_CUSTOM_FONT = "switzer";

const CUSTOM_FONT_OPTIONS = [
  { value: "switzer", label: "Switzer", fontFamily: "switzer-regular" },
  { value: "lato", label: "Lato", fontFamily: "lato-regular" },
  { value: "openSans", label: "Open Sans", fontFamily: "openSans-regular" },
  { value: "montserrat", label: "Montserrat", fontFamily: "montserrat-regular" },
  { value: "inter", label: "Inter", fontFamily: "inter-regular" },
  { value: "oswald", label: "Oswald", fontFamily: "oswald-regular" },
  { value: "tajawal", label: "Tajawal", fontFamily: "tajawal-regular" },
];

const getCustomFontFamily = (customFont) => {
  const selectedFont = CUSTOM_FONT_OPTIONS.find((option) => option.value === customFont);
  return selectedFont?.fontFamily || "inherit";
};

const DEFAULT_CUSTOM_LANG = {
  en: {
    titleWelcome: "Hello! We hope you enjoy your stay with us",
  },
  es: {
    titleWelcome: "¡Hola! Esperamos que disfrutes de tu estancia con nosotros",
  },
  fr: {
    titleWelcome: "Bonjour ! Nous espérons que vous passerez un agréable séjour parmi nous.",
  },
};

const withDefaultText = (value, defaultValue) => {
  const textValue = String(value || "").trim();
  return textValue || defaultValue;
};

const hasColorValue = (value) => !!String(value || "").trim();

const isValidHex = (value) => /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(String(value || "").trim());

const validate = (values) => {
  const errors = {};

  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  if (values.useCustomWelcomeBackgroundColor) {
    if (values.customWelcomeBackgroundColor && !isValidHex(values.customWelcomeBackgroundColor)) {
      errors.customWelcomeBackgroundColor = "Invalid color (use #RRGGBB)";
    }
  }

  if (values.useCustomIconColor) {
    if (values.customIconColor && !isValidHex(values.customIconColor)) {
      errors.customIconColor = "Invalid color (use #RRGGBB)";
    }
  }

  if (values.useCustomAppBackgroundColor) {
    if (values.customAppBackgroundColor && !isValidHex(values.customAppBackgroundColor)) {
      errors.customAppBackgroundColor = "Invalid color (use #RRGGBB)";
    }
  }

  if (values.useCustomCardColor) {
    if (values.customCardColor && !isValidHex(values.customCardColor)) {
      errors.customCardColor = "Invalid color (use #RRGGBB)";
    }
  }

  if (values.useCustomFont) {
    const selectedCustomFont = String(values.customFont || "").trim();
    const validFont = CUSTOM_FONT_OPTIONS.some((option) => option.value === selectedCustomFont);

    if (!selectedCustomFont || !validFont) {
      errors.customFont = "Select a valid font";
    }
  }

  return errors;
};

function CustomAppMobile({
  handleSubmit,
  pristine,
  actNewAlertMessage,
  actChangeField,
  database,
  projectId,
  customWelcomeBackgroundColorValue,
  customIconColorValue,
  customAppBackgroundColorValue,
  customCardColorValue,
  customFontValue,
}) {
  const { t } = useTranslation();

  const editDataAndSubmit = async (data) => {
    const {
      titleWelcome_en,
      titleWelcome_es,
      titleWelcome_fr,

      useCustomWelcomeBackgroundColor,
      customWelcomeBackgroundColor,
      useCustomIconColor,
      customIconColor,
      useCustomAppBackgroundColor,
      customAppBackgroundColor,
      useCustomCardColor,
      customCardColor,
      useCustomFont,
      customFont,
    } = data;

    const finalWelcomeBg = String(customWelcomeBackgroundColor || "").trim() || null;
    const finalIconColor = String(customIconColor || "").trim() || null;
    const finalAppBackgroundColor = String(customAppBackgroundColor || "").trim() || null;
    const finalCardColor = String(customCardColor || "").trim() || null;
    const finalCustomFont = String(customFont || "").trim() || null;

    const dataToSend = {
      projectId,

      customLang: {
        en: {
          titleWelcome: withDefaultText(titleWelcome_en, DEFAULT_CUSTOM_LANG.en.titleWelcome),
        },
        es: {
          titleWelcome: withDefaultText(titleWelcome_es, DEFAULT_CUSTOM_LANG.es.titleWelcome),
        },
        fr: {
          titleWelcome: withDefaultText(titleWelcome_fr, DEFAULT_CUSTOM_LANG.fr.titleWelcome),
        },
      },

      useCustomWelcomeBackgroundColor: !!useCustomWelcomeBackgroundColor,
      customWelcomeBackgroundColor: finalWelcomeBg,
      useCustomIconColor: !!useCustomIconColor,
      customIconColor: finalIconColor,
      useCustomAppBackgroundColor: !!useCustomAppBackgroundColor,
      customAppBackgroundColor: finalAppBackgroundColor,
      useCustomCardColor: !!useCustomCardColor,
      customCardColor: finalCardColor,
      useCustomFont: !!useCustomFont,
      customFont: finalCustomFont,
    };

    try {
      await database.editCustomLangInProject(dataToSend);
      actNewAlertMessage({
        type: "success",
        title: t("customApp"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("customApp"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(editDataAndSubmit)}>
      <Card id="basic-info" sx={{ overflow: "visible" }}>
        <MDBox p={3}>
          <MDTypography variant="h4">{t("customApp").toUpperCase()}</MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <MDBox mt={1} pt={0}>
            <MDTypography variant="h4">{t("Custom Font in App")}</MDTypography>
          </MDBox>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Field
                name="useCustomFont"
                component={({ input }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!input.value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          input.onChange(checked);

                          if (checked && !String(customFontValue || "").trim()) {
                            actChangeField(FORM_NAME, "customFont", DEFAULT_CUSTOM_FONT);
                          }
                        }}
                      />
                    }
                    label={t("customFont")}
                  />
                )}
              />
            </Grid>

            <Field
              name="useCustomFont"
              component={({ input }) =>
                input.value ? (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Field
                      name="customFont"
                      component={({ input: fontInput, meta }) => (
                        <MDInput
                          select
                          fullWidth
                          variant="standard"
                          label={t("customFont")}
                          value={fontInput.value || ""}
                          onChange={fontInput.onChange}
                          onBlur={fontInput.onBlur}
                          error={meta.touched && !!meta.error}
                          helperText={meta.touched && meta.error ? t(meta.error) : ""}
                          sx={{
                            "& .MuiSelect-select": {
                              fontFamily: getCustomFontFamily(fontInput.value),
                              fontSize: "1.4rem",
                            },
                          }}
                          SelectProps={{
                            displayEmpty: true,
                          }}
                        >
                          {CUSTOM_FONT_OPTIONS.map((option) => (
                            <MenuItem
                              sx={{ fontFamily: option.fontFamily, fontSize: "1.4rem" }}
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </MDInput>
                      )}
                    />
                  </Grid>
                ) : null
              }
            />
          </Grid>
          <Grid container spacing={3} mt={3}>
            <MDBox mt={1} pt={0}>
              <MDTypography variant="h4">{t("Custom Colors in App")}</MDTypography>
            </MDBox>

            <Grid size={{ xs: 12 }}>
              <Field
                name="useCustomIconColor"
                component={({ input }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!input.value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          input.onChange(checked);

                          if (checked && !hasColorValue(customIconColorValue)) {
                            actChangeField(FORM_NAME, "customIconColor", DEFAULT_ICON_COLOR);
                          }
                        }}
                      />
                    }
                    label={t("customIconColor")}
                  />
                )}
              />
            </Grid>
            <Field
              name="useCustomIconColor"
              component={({ input }) =>
                input.value ? (
                  <Grid size={{ xs: 12 }}>
                    <Field
                      name="customIconColor"
                      component={ColorPickerField}
                      label={t("customIconColor")}
                      placeholder={DEFAULT_ICON_COLOR}
                    />
                  </Grid>
                ) : null
              }
            />
            <Grid size={{ xs: 12 }}>
              <Field
                name="useCustomAppBackgroundColor"
                component={({ input }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!input.value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          input.onChange(checked);

                          if (checked && !hasColorValue(customAppBackgroundColorValue)) {
                            actChangeField(
                              FORM_NAME,
                              "customAppBackgroundColor",
                              DEFAULT_APP_BACKGROUND_COLOR
                            );
                          }
                        }}
                      />
                    }
                    label={t("customAppBackgroundColor")}
                  />
                )}
              />
            </Grid>
            <Field
              name="useCustomAppBackgroundColor"
              component={({ input }) =>
                input.value ? (
                  <Grid size={{ xs: 12 }}>
                    <Field
                      name="customAppBackgroundColor"
                      component={ColorPickerField}
                      label={t("customAppBackgroundColor")}
                      placeholder={DEFAULT_APP_BACKGROUND_COLOR}
                    />
                  </Grid>
                ) : null
              }
            />

            <Grid size={{ xs: 12 }}>
              <Field
                name="useCustomCardColor"
                component={({ input }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!input.value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          input.onChange(checked);

                          if (checked && !hasColorValue(customCardColorValue)) {
                            actChangeField(FORM_NAME, "customCardColor", DEFAULT_CARD_COLOR);
                          }
                        }}
                      />
                    }
                    label={t("customCardColor")}
                  />
                )}
              />
            </Grid>
            <Field
              name="useCustomCardColor"
              component={({ input }) =>
                input.value ? (
                  <Grid size={{ xs: 12 }}>
                    <Field
                      name="customCardColor"
                      component={ColorPickerField}
                      label={t("customCardColor")}
                      placeholder={DEFAULT_CARD_COLOR}
                    />
                  </Grid>
                ) : null
              }
            />
          </Grid>

          <MDBox mt={2} pt={1}>
            <MDTypography variant="h4">{t("Custom Welcome Mobile")}</MDTypography>
          </MDBox>

          <Grid container spacing={3} mt={0.5}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Field name="titleWelcome_en" component={FormField} label={t("Welcome EN")} />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <Field name="titleWelcome_es" component={FormField} label={t("Welcome ES")} />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <Field name="titleWelcome_fr" component={FormField} label={t("Welcome FR")} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Field
                name="useCustomWelcomeBackgroundColor"
                component={({ input }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!input.value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          input.onChange(checked);

                          if (checked && !hasColorValue(customWelcomeBackgroundColorValue)) {
                            actChangeField(
                              FORM_NAME,
                              "customWelcomeBackgroundColor",
                              DEFAULT_WELCOME_BG
                            );
                          }
                        }}
                      />
                    }
                    label={t("customWelcomeBackgroundColor")}
                  />
                )}
              />
            </Grid>
            <Field
              name="useCustomWelcomeBackgroundColor"
              component={({ input }) =>
                input.value ? (
                  <Grid size={{ xs: 12 }}>
                    <Field
                      name="customWelcomeBackgroundColor"
                      component={ColorPickerField}
                      label={t("welcomeBackgroundColor")}
                      placeholder={DEFAULT_WELCOME_BG}
                    />
                  </Grid>
                ) : null
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
              <MDButton
                type="submit"
                variant="gradient"
                color="dark"
                size="small"
                disabled={pristine}
              >
                {t("Save")}
              </MDButton>
            </MDBox>
          </Grid>
        </MDBox>
      </Card>
    </form>
  );
}

CustomAppMobile.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  actChangeField: PropTypes.func.isRequired,
  database: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,

  customWelcomeBackgroundColorValue: PropTypes.string,
  customIconColorValue: PropTypes.string,
  customAppBackgroundColorValue: PropTypes.string,
  customCardColorValue: PropTypes.string,
  customFontValue: PropTypes.string,
};

CustomAppMobile.defaultProps = {
  customWelcomeBackgroundColorValue: "",
  customIconColorValue: "",
  customAppBackgroundColorValue: "",
  customCardColorValue: "",
  customFontValue: "",
};

const formSelector = formValueSelector(FORM_NAME);

const mapStateToProps = (state, props) => {
  const { mainUserReducer, projectsReducer } = state;
  const { projectId } = props;

  const customApp = projectsReducer[projectId]?.customAppConfig ?? {};
  const {
    customLang = {},
    customWelcomeBackgroundColor,
    useCustomWelcomeBackgroundColor,
    customIconColor,
    useCustomIconColor,
    customAppBackgroundColor,
    useCustomAppBackgroundColor,
    customCardColor,
    useCustomCardColor,
    customFont,
    useCustomFont,
  } = customApp;

  const mergedCustomLang = Object.keys(DEFAULT_CUSTOM_LANG).reduce((acc, language) => {
    const currentLang = customLang?.[language] ?? {};

    acc[language] = {
      titleWelcome: withDefaultText(
        currentLang.titleWelcome,
        DEFAULT_CUSTOM_LANG[language].titleWelcome
      ),
    };

    return acc;
  }, {});

  const initialValues = {};

  if (mergedCustomLang != null) {
    Object.keys(mergedCustomLang).forEach((language) => {
      const langObj = mergedCustomLang[language] ?? {};
      Object.keys(langObj).forEach((languageKey) => {
        initialValues[`${languageKey}_${language}`] = langObj[languageKey];
      });
    });
  }

  initialValues.useCustomWelcomeBackgroundColor = !!useCustomWelcomeBackgroundColor;
  initialValues.customWelcomeBackgroundColor = customWelcomeBackgroundColor ?? "";

  initialValues.useCustomIconColor = !!useCustomIconColor;
  initialValues.customIconColor = customIconColor ?? "";

  initialValues.useCustomAppBackgroundColor = !!useCustomAppBackgroundColor;
  initialValues.customAppBackgroundColor = customAppBackgroundColor ?? "";

  initialValues.useCustomCardColor = !!useCustomCardColor;
  initialValues.customCardColor = customCardColor ?? "";

  initialValues.useCustomFont = !!useCustomFont;
  initialValues.customFont = customFont ?? "";

  return {
    initialValues,
    user: mainUserReducer,
    customWelcomeBackgroundColorValue: formSelector(state, "customWelcomeBackgroundColor"),
    customIconColorValue: formSelector(state, "customIconColor"),
    customAppBackgroundColorValue: formSelector(state, "customAppBackgroundColor"),
    customCardColorValue: formSelector(state, "customCardColor"),
    customFontValue: formSelector(state, "customFont"),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actChangeField: (form, field, value) => dispatch(changeFieldValue(form, field, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    validate,
  })(CustomAppMobile)
);
